import { TextField, Typography, styled } from "@mui/material"
import {
  AssetType,
  IndustryType,
  type AssetGroup,
} from "@phc/common/gen/preferencesGen"

import type { GeocoderResult } from "@phc/common"
import type React from "react"
import { useCallback, useState } from "react"
import type { Asset, AssetInput } from "../../../../utils/preferencesApi"

import { getLocationId } from "../../../../utils/helpers"
import {
  convertGeocoderResultToLocation,
  isAdminBoundaryFromPlaceType,
} from "../../../../utils/helpers/assetHelper"
import { getCentroidFromGeocoder } from "../../../../utils/helpers/locationCenterHelper"
import { toAssetInput } from "../../../../utils/preferencesApi"
import { extraColors } from "../../../../utils/theme"
import { AssetGroupAutocomplete } from "../../../WatchedLocations/AssetManagement/AssetGroupAutocomplete"
import { AssetTypeAutocomplete } from "../../../WatchedLocations/AssetManagement/AssetTypeAutocomplete"
import { AssetGeoLocationSearchInput } from "../../../WatchedLocations/AssetManagement/GeoLocationSearchInput"
import { IndustryAutocomplete } from "../../../WatchedLocations/AssetManagement/IndustryAutocomplete"
import { AssetDialogControls } from "../../../WatchedLocations/AssetManagement/Shared"
import { useUpdateAsset } from "../../../WatchedLocations/AssetManagement/hooks/useUpdateAsset"

const MAX_NOTES_CHARACTERS = 250

const AssetEditContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 20,
  marginTop: 20,
})

const NotesContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
})

interface EditSiteDetailsgProps {
  asset: Asset
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>
}

export const EditSiteDetails: React.FC<EditSiteDetailsgProps> = ({
  asset,
  setIsEditing,
}) => {
  const [selectedGroups, setSelectedGroups] = useState<
    AssetGroup[] | undefined
  >(asset.asset_groups ?? undefined)
  const [selectedAssetType, setSelectedAssetType] = useState<
    AssetType | undefined
  >(asset.asset_types?.[0] ?? AssetType.ASSET_TYPE_UNSPECIFIED)

  const { mutateAsync: update, isPending: updateIsLoading } = useUpdateAsset()
  const [location_id, setLocationId] = useState(getLocationId(asset))
  const [name, setName] = useState(asset.name ?? "")
  const [notes, setNotes] = useState(asset.notes ?? "")

  const [selectedIndustryType, setSelectedIndustryType] = useState<
    IndustryType | undefined
  >(asset.industry ?? IndustryType.INDUSTRY_TYPE_UNSPECIFIED)
  const [groups, setGroups] = useState<AssetGroup[] | undefined>(
    asset.asset_groups ?? undefined
  )
  const [location, setLocation] = useState<GeocoderResult | undefined>()
  const [isAdminBoundary, setIsAdminBoundary] = useState(
    isAdminBoundaryFromPlaceType(
      asset.base_event?.geotags[0]?.mapbox_location?.place_type ?? undefined
    )
  )

  const saveAsset = useCallback(async () => {
    const existingGeotag = asset.base_event?.geotags[0]

    const newMapboxLocation = location
      ? convertGeocoderResultToLocation(location)
      : existingGeotag?.mapbox_location

    const mergedGeotag = {
      ...existingGeotag,
      location_id: location_id ?? existingGeotag?.location_id ?? "",
      mapbox_location: newMapboxLocation,
      wkt_centroid:
        getCentroidFromGeocoder(location) ?? existingGeotag?.wkt_centroid,
    }

    const newAsset: AssetInput = {
      ...toAssetInput(asset),
      asset_id: asset.asset_id,
      notification_settings: asset.notification_settings,
      name,
      notes,
      industry: selectedIndustryType,
      asset_groups: selectedGroups ?? [],
      asset_types: [selectedAssetType ?? AssetType.ASSET_TYPE_UNSPECIFIED],
      threat_radius: asset.threat_radius,
      base_event: {
        mapbox_location: asset.base_event?.mapbox_location,
        geotags: [mergedGeotag],
      },
    }

    await update({ asset: newAsset })
  }, [
    asset,
    location,
    location_id,
    name,
    notes,
    selectedAssetType,
    selectedGroups,
    selectedIndustryType,
    update,
  ])

  return (
    <AssetEditContainer>
      <TextField
        id="outlined"
        label="Nickname"
        value={name}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setName(event.target.value)
        }}
      />

      <AssetGeoLocationSearchInput
        onChangeGeo={setLocation}
        setLocationId={setLocationId}
        placeholder={
          asset.base_event?.geotags[0]?.mapbox_location?.place_name ?? undefined
        }
        setIsAdminBoundary={setIsAdminBoundary}
        setName={setName}
      />

      <IndustryAutocomplete
        selectedIndustry={
          selectedIndustryType ?? IndustryType.INDUSTRY_TYPE_UNSPECIFIED
        }
        setSelectedIndustry={setSelectedIndustryType}
        isDisabled={isAdminBoundary}
      />

      <AssetGroupAutocomplete
        groups={groups ?? []}
        setGroups={setGroups}
        selectedGroups={selectedGroups}
        setSelectedGroups={setSelectedGroups}
      />

      <AssetTypeAutocomplete
        selectedAssetType={
          selectedAssetType ?? AssetType.ASSET_TYPE_UNSPECIFIED
        }
        setSelectedAssetType={setSelectedAssetType}
        isDisabled={isAdminBoundary}
      />

      <NotesContainer>
        <TextField
          id="outlined"
          label="Notes"
          multiline
          value={notes}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setNotes(event.target.value)
          }}
          slotProps={{
            htmlInput: { maxLength: MAX_NOTES_CHARACTERS },
          }}
        />
        <Typography
          variant="small1"
          sx={{
            marginTop: "6px",
            alignSelf: "flex-end",
            color: extraColors.medium,
          }}
        >
          {notes.length}/{MAX_NOTES_CHARACTERS}
        </Typography>
      </NotesContainer>

      <AssetDialogControls
        handleModalClose={() => setIsEditing(false)}
        isDisabled={
          updateIsLoading ||
          (!location &&
            !asset.base_event?.geotags[0]?.mapbox_location?.place_type)
        }
        action={saveAsset}
        text={"Save Changes"}
      />
    </AssetEditContainer>
  )
}
